/**
 * Generic error screen used by the router's `errorComponent`.
 *
 * Renders:
 *   - A clear headline + explanation
 *   - The request ID (if the error came from ApiError) so the
 *     operator can hand it to support
 *   - "Try again" (resets the router's error boundary)
 *   - "Copy details" (copies a structured blob — headline +
 *     request-id + message + stack truncated to 20 lines — to the
 *     clipboard, because support issues reference error text more
 *     often than screenshots)
 *
 * The screen is intentionally *not* the login page's chrome: an
 * error is not a credential prompt, and reusing the auth-shell
 * would be confusing.
 */

import { useState } from "react";
import { AlertTriangle, Copy, Home, RotateCcw } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button, SignedOutScreen } from "@pureid/ui";
import { toast } from "sonner";
import { ApiError } from "@/lib/api-client";

interface Props {
  error: unknown;
  reset?: () => void;
  /** Override the headline for specific cases (e.g. "Page not found"). */
  title?: string;
  /** Override the body copy. */
  description?: string;
}

export function ErrorScreen({ error, reset, title, description }: Props) {
  const summary = summarize(error);
  const [copied, setCopied] = useState(false);

  // A 401 at this layer means the user's session just died mid-
  // use (bearer invalid, idle timeout, admin-revoked, expired).
  // Route it to the purpose-built SignedOutScreen: calm chrome,
  // a clear "Sign in" primary action, and technical details
  // tucked behind a "Need help?" disclosure. The generic fall-
  // back below still handles 403/404/500 where "Try again" and
  // "Back to dashboard" are meaningful.
  //
  // The `title` override escape lets callers that want a custom
  // headline (e.g. the route-level not-found boundary) keep the
  // generic chrome — they won't be hitting 401 anyway.
  if (summary.status === 401 && !title) {
    return <SignedOutScreen requestId={summary.requestId} />;
  }

  async function copyDetails() {
    try {
      const blob = [
        `PureID admin error report`,
        `Time: ${new Date().toISOString()}`,
        `URL: ${typeof window !== "undefined" ? window.location.href : "unknown"}`,
        summary.requestId ? `Request ID: ${summary.requestId}` : null,
        `Status: ${summary.status ?? "n/a"}`,
        `Message: ${summary.message}`,
        summary.stack ? `\nStack:\n${summary.stack}` : null,
      ]
        .filter(Boolean)
        .join("\n");
      await navigator.clipboard.writeText(blob);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard access denied. Copy the message manually.");
    }
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="mx-auto h-14 w-14 rounded-xl border border-danger/30 bg-danger/10 flex items-center justify-center">
          <AlertTriangle className="h-7 w-7 text-danger" aria-hidden />
        </div>
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {title ?? defaultTitle(summary)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {description ?? defaultDescription(summary)}
          </p>
        </div>

        <div className="ui-card rounded-xl border border-border/60 bg-card p-4 text-left space-y-2">
          <DetailRow label="Message" value={summary.message} />
          {summary.status !== null && (
            <DetailRow label="Status" value={String(summary.status)} />
          )}
          {summary.requestId && (
            <DetailRow label="Request ID" value={summary.requestId} mono />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {reset && (
            <Button onClick={reset} size="sm">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Try again
            </Button>
          )}
          <Link to="/">
            <Button variant="outline" size="sm">
              <Home className="h-3.5 w-3.5 mr-1.5" /> Back to dashboard
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={copyDetails}>
            <Copy className="h-3.5 w-3.5 mr-1.5" />
            {copied ? "Copied" : "Copy details"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

interface ErrorSummary {
  message: string;
  status: number | null;
  requestId: string | null;
  stack: string | null;
}

function summarize(error: unknown): ErrorSummary {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      status: error.status,
      requestId: error.requestId,
      stack: truncateStack(error.stack),
    };
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      status: null,
      requestId: null,
      stack: truncateStack(error.stack),
    };
  }
  return {
    message: String(error),
    status: null,
    requestId: null,
    stack: null,
  };
}

function truncateStack(stack: string | undefined): string | null {
  if (!stack) return null;
  const lines = stack.split("\n");
  return lines.slice(0, 20).join("\n");
}

function defaultTitle(summary: ErrorSummary): string {
  if (summary.status === 401) return "You're signed out";
  if (summary.status === 403) return "Access denied";
  if (summary.status === 404) return "Not found";
  if (summary.status && summary.status >= 500) return "Something went wrong";
  return "Something went wrong";
}

function defaultDescription(summary: ErrorSummary): string {
  if (summary.status === 401)
    return "Your session has ended. Sign in again to continue.";
  if (summary.status === 403)
    return "You don't have permission for this action. Ask your administrator for access.";
  if (summary.status === 404)
    return "We couldn't find what you were looking for.";
  if (summary.status && summary.status >= 500)
    return "The server hit an unexpected error. We've logged it — please try again, and if it keeps failing, paste the request ID into a support ticket.";
  return "An unexpected error occurred. You can retry or head back to the dashboard.";
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 text-xs">
      <span className="w-24 shrink-0 uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span
        className={
          "flex-1 text-foreground " + (mono ? "font-mono break-all" : "")
        }
      >
        {value}
      </span>
    </div>
  );
}
